import { queryClient, unifiedApiClient } from 'merchant-api';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { BlueBlob, Button, Card, formatPhoneNumber, FormattedMessageDiv, FormInput, FormWrapper, Modal } from '@odmonorepo/core-components';
import { Col, Row, Stack } from 'react-bootstrap';
import * as yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { markIntakeBridge } from './intakeBridge';
import { useAnalytics } from 'use-analytics';
import type { paths } from 'merchant-api/unified/oas';
import { formatDateInput, formatToISODate, validateDateComplete, validateDateFormat, validateNotFuture } from '@odmonorepo/odfunctions';
import { getProveStartFallbackReason, trackProvePrefillFallback, type ProveFlowType } from '../Prove/proveAnalytics';
import { getProveFallbackError } from '../Prove/useProveAuthenticator';

const schema = yup.object().shape({
  email: yup.string().required('formInput.email.error').email('formInput.email.invalid'),
  dateOfBirth: yup
    .string()
    .required('formInput.dateOfBirth.error')
    .test('must be complete date', 'formInput.dateOfBirth.error', validateDateComplete)
    .test('must be valid date', 'formInput.dateOfBirth.error.invalidFormat', validateDateFormat)
    .test('must be in past', 'formInput.dateOfBirth.error.futureDate', validateNotFuture),
  businessName: yup.string().trim().required('formInput.party.businesses.legalName.error'),
  homePhone: yup
    .string()
    .required('formInput.homePhone.error')
    .test('is-10-digits', 'formInput.homePhone.invalid', (val) => val?.replace(/\D/g, '').length === 10),
});

type ProveStartResponse = paths['/client/prove/start']['post']['responses'][200]['content']['application/json'] & {
  associate?: boolean;
  flowType?: ProveFlowType;
};

interface ProveRequestError {
  status?: number;
  data?: unknown;
}

type CreatedApplication = { id: string };

type ProveEntryInput = {
  create: Record<string, unknown>;
  start: { businessName: string; dateOfBirth: string; email: string; flowType: ProveFlowType; phoneNumber: string; provePrefillConsent: string };
};

type ProveEntryResult =
  | { kind: 'prove'; application: CreatedApplication; session: ProveStartResponse }
  | { kind: 'fallback'; application: CreatedApplication; error?: string };

type ProveFallbackState = { action: 'continue'; appId: string };

function getReferralSourceFromUrl(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('referralSource');
    if (!raw) return null;
    const trimmed = raw.trim();
    return trimmed.length ? trimmed : null;
  } catch {
    return null;
  }
}

function getOfferCheckSumFromUrl(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('offerCheckSum');
    if (!raw) return null;
    const trimmed = raw.trim();
    return trimmed.length ? trimmed : null;
  } catch {
    return null;
  }
}

// these are temporary until we have the unified API client fully implemented.
function isProveStartResponse(data: unknown): data is ProveStartResponse {
  return ['authToken', 'key'].every((key) => typeof (data as Record<string, unknown> | null)?.[key] === 'string');
}

function storeProveSessionData(data: ProveStartResponse, phoneNumber?: string) {
  try {
    window.sessionStorage.setItem(`proveAuthToken:${data.key}`, data.authToken);
    if (phoneNumber) window.sessionStorage.setItem(`provePhoneNumber:${data.key}`, phoneNumber);
  } catch {
    // The mobile auth route will surface a recoverable error if storage is unavailable.
  }
}

function getProveFlowType(): ProveFlowType {
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
  if (isMobile) return 'mobile';
  return 'desktop';
}

function getAuthRedirect(data: { appId?: string; associate?: boolean; redirect?: string }) {
  if (data.redirect?.trim()) return data.redirect;
  if (data.appId) return `/application/${data.appId}`;

  return '/application';
}

function toProveSearch(data: ProveStartResponse) {
  return {
    associate: data.associate === true,
    key: data.key,
    flowType: data.flowType ?? getProveFlowType(),
    phoneMasked: data.phoneMasked,
    redirect: getAuthRedirect(data),
  };
}

// Create only creates (XF-10207): it returns the application and grants the app-token session, and
// Prove is started separately on that session. A start failure is not an error for the applicant —
// they continue into the wizard manually, already authed, exactly like the in-wizard PROVE_STEP.
// Create failures still throw: without an application there is nothing to continue into.
const createAppStartProve = async ({ create, start }: ProveEntryInput): Promise<ProveEntryResult> => {
  const created = await unifiedApiClient.POST('/public/application/v1/create', { body: create } as never);

  if (!created.response.ok) throw { status: created.response.status, data: created.error } satisfies ProveRequestError;

  const application = created.data as unknown as CreatedApplication | undefined;
  if (!application?.id) throw { status: created.response.status, data: created.data } satisfies ProveRequestError;

  // The unified client throws on every 4xx/5xx (UnifiedApiError, or AppError.Unauthenticated for
  // 401/403 on /client routes), so a failed start arrives here as an exception, never as a response.
  try {
    const started = await unifiedApiClient.POST('/client/prove/start' as never, { body: { ...start, appId: application.id } } as never);

    if (!isProveStartResponse(started.data)) return { kind: 'fallback', application };

    return { kind: 'prove', application, session: started.data };
  } catch (error) {
    return { kind: 'fallback', application, error: getProveFallbackError(error) };
  }
};

export interface ProveBasicInfoProps {
  onOptOut: () => void;
}

export const ProveBasicInfo = ({ onOptOut }: ProveBasicInfoProps) => {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const referralSourceFromQuery = useMemo(() => getReferralSourceFromUrl(), []);
  const offerCheckSumFromQuery = useMemo(() => getOfferCheckSumFromUrl(), []);
  const [proveFallback, setProveFallback] = useState<ProveFallbackState>();
  const [showModal, setShowModal] = useState(false);
  const handleConfirm = () => setShowModal(false);

  const { mutate, isPending, error } = useMutation({
    mutationFn: createAppStartProve,
    onSuccess: (result, variables) => {
      markIntakeBridge();
      if (result.kind === 'fallback') {
        trackProvePrefillFallback({
          fallbackAction: 'continue',
          fallbackReason: getProveStartFallbackReason(result.error) ?? 'ProveUnavailable',
          flowType: variables.start.flowType,
        });
        setProveFallback({ action: 'continue', appId: result.application.id });
        return;
      }

      const session = { ...result.session, appId: result.application.id };
      storeProveSessionData(session, variables.start.phoneNumber);
      queryClient.setQueryData(['proveAuth', session.key], session);

      return navigate({
        to: '/login/mobile',
        search: toProveSearch(session),
      });
    },
  });

  const handleSubmit = (values: any) => {
    if (isPending) return;
    const referralSourceToUse = referralSourceFromQuery ?? window.location.href;
    const flowType = getProveFlowType();
    // Legacy M1/M2 semantics: consent is recorded on the application at create (this path is
    // measured against that); prove/complete writes the same value again, which is idempotent.
    const provePrefillConsent = new Date().toISOString();

    mutate({
      create: {
        ...values,
        flowType,
        referralSource: referralSourceToUse,
        ...(offerCheckSumFromQuery ? { offerCheckSum: offerCheckSumFromQuery } : {}),
        channelCode: 'DIRECT',
        provePrefillConsent,
      },
      start: {
        businessName: String(values.businessName ?? '').trim(),
        dateOfBirth: formatToISODate(values.dateOfBirth),
        email: String(values.email ?? '').trim(),
        flowType,
        phoneNumber: String(values.homePhone ?? '').replace(/\D/g, ''),
        provePrefillConsent,
      },
    });
  };

  // Already authed by create's app token: straight into the wizard, no OTP detour.
  const handleFallbackAction = () => {
    if (!proveFallback) return;

    return navigate({ to: '/application/$id/apply', params: { id: proveFallback.appId } });
  };

  const OptOutLink = () => (
    <div className="text-center">
      <button type="button" onClick={onOptOut} className="btn btn-link fs-7 p-0">
        Enter information manually
      </button>
    </div>
  );

  const HowWeVerifyModalTitle = () =>
  <FormattedMessageDiv id={`formInput.phoneNumberCell.modal.title`} />

  const HowWeVerifyModalBody = () =>
  <FormattedMessageDiv id={`formInput.phoneNumberCell.modal.body`} values={{
    p1: (txt: any) => <p>{txt}</p>,
    p2: (txt: any) => <p>{txt}</p>,
    terms: (txt: any) => <a rel="noreferrer" href="https://www.ondeck.com/terms">{txt}</a>,
    privacy: (txt: any) => <a rel="noreferrer" href="https://www.ondeck.com/privacy">{txt}</a>,
  }}/>
  

  useEffect(() => {
    analytics.track('FirstStepLoaded', {
      fromStep: 'BASIC_INFO',
      subStep: 'PROVE_ENTRY',
    });
    // Emit per-step analytics for the intake funnel.
    analytics.track('StepLoaded', { step: 'BASIC_INFO', stepOrder: 1, flowType: 'direct_prove_prefill', subStep: 'PROVE_ENTRY' });
  }, []);

  // Non-success responses show the generic error page instead of redirecting to ODO.
  if (error) {
    throw error instanceof Error
      ? error
      : new Error(`Unable to create application (status ${(error as { status?: number })?.status ?? 'unknown'})`);
  }

  const activeFallbackMessagePrefix = 'proveBasicInfo.fallback.continue';

  return (
    <FormWrapper key="entry-phase" schema={schema} onSubmit={handleSubmit}>
      <Stack gap={6} style={{ maxWidth: '600px', margin: 'auto' }}>
        <BlueBlob>
          <FormattedMessageDiv className="h3" id="basicInfo.title" />
        </BlueBlob>
        {proveFallback ? (
          <Card>
            <Stack gap={5}>
              <div>
                <FormattedMessageDiv className="h4 mb-2" id={`${activeFallbackMessagePrefix}.title`} />
                <FormattedMessageDiv id={`${activeFallbackMessagePrefix}.description`} />
              </div>
              <Button
                loading={isPending}
                disabled={isPending}
                type="button"
                onClick={handleFallbackAction}
              >
                <FormattedMessageDiv id={`${activeFallbackMessagePrefix}.button`} />
              </Button>
            </Stack>
          </Card>
        ) : (
          <Card>
            <Stack gap={5}>
              <div>
                <h4 className="mb-2">Let&apos;s find your info.</h4>
                <p className="mb-0 fs-7">We&apos;ll autofill some of your application using the following information.</p>
              </div>
              <Row>
                <Col>
                  <FormInput name="email" data-testid="email" label="Email Address" useYupErrors controlProps={{ inputMode: 'email' }} />
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormInput 
                    name="homePhone"
                    data-testid="homePhone"
                    label="Personal Cell Phone Number"
                    className="label-multiline"
                    helperText={
                      <FormattedMessageDiv
                        id="formInput.phoneNumberCell.helperText"
                        values={{
                          link: (txt: React.ReactNode) => (
                            <button type="button" className="btn btn-link p-0 fs-8 align-baseline" onClick={() => setShowModal(true)}>{txt}</button>
                          ),
                        }}
                      />
                    }
                    formatValue={formatPhoneNumber}
                    controlProps={{ inputMode: 'tel' }}
                    useYupErrors
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormInput
                    name="dateOfBirth"
                    data-testid="dateOfBirth"
                    label={<FormattedMessageDiv id="formInput.dateOfBirth" />}
                    helperText={<FormattedMessageDiv id="formInput.dateOfBirth.helperText" />}
                    formatValue={formatDateInput}
                    useYupErrors
                    controlProps={{ inputMode: 'numeric' }}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormInput name="businessName" data-testid="businessName" label="Legal Business Name" useYupErrors />
                </Col>
              </Row>
              <Button loading={isPending} disabled={isPending} type="submit">
                Continue
              </Button>
              <OptOutLink />
            </Stack>
          </Card>
        )}
        {!proveFallback && (
          <FormattedMessageDiv
            className="fs-8"
            id="basicInfo.disclaimer.submit"
            values={{
              terms: (txt: React.ReactNode) => (
                <a target="_blank" rel="noreferrer" href="https://www.ondeck.com/terms">
                  {txt}
                </a>
              ),
              privacy: (txt: React.ReactNode) => (
                <a target="_blank" rel="noreferrer" href="https://www.ondeck.com/privacy">
                  {txt}
                </a>
              ),
            }}
          />
        )}
      </Stack>
      <Modal
        showModal={showModal}
        handleClose={() => setShowModal(false)}
        title={<HowWeVerifyModalTitle />}
        onButtonClick={handleConfirm}
      >
        <HowWeVerifyModalBody />
      </Modal>
    </FormWrapper>
  );
};

export default ProveBasicInfo;
